import React from 'react';

import type { ConversationDocument } from './documentMentions';

/**
 * Ein Dokument öffnen, von überall: `useDocumentViewer()?.openDocument({ fileId, page })`.
 *
 * Eine Stelle für alle Wege dorthin — Quellenangaben in der Antwort, die Quellenliste unter ihr,
 * die Dateiverwaltung —, damit es genau einen Betrachter gibt und er beim nächsten Klick das Dokument
 * wechselt, statt einen zweiten zu öffnen. Das Panel samt pdf.js wird erst beim ersten Öffnen
 * geladen.
 */
export interface OpenDocumentRequest {
    /** Die `ManagedFile`-Id. */
    fileId: string;
    filename?: string;
    /** Die Seite, auf der der Betrachter öffnet; ohne sie Seite 1. */
    page?: number;
    /** Ein Link zum Original, etwa in SharePoint. */
    webUrl?: string;
    /** Bei einem Normtext-Fragment: das ganze Regelwerk — Webseite oder unser Gesamttext. */
    reference?: { label: string; url?: string; fileId?: string };
}

interface DocumentViewerApi {
    openDocument: (request: OpenDocumentRequest) => void;
}

const DocumentViewerContext = React.createContext<DocumentViewerApi | null>(null);

/** Null außerhalb des Providers — Aufrufer fallen dann auf ihr bisheriges Verhalten zurück. */
export const useDocumentViewer = () => React.useContext(DocumentViewerContext);

/**
 * Zeigt der Betrachter diese Datei? Nur PDFs — alles andere bietet er nur zum Herunterladen an, und
 * ein „Ansehen", das dann nur einen Download-Knopf zeigt, ist ein Umweg. Die Endung zählt vor dem
 * MIME-Typ, weil der Upload-Pfad dem Namen traut. Ein **Dateiname, keine URL**: `#` gehört zum
 * Namen („002_005#Reinigungsvertrag.pdf" — gecrawlte Unterlagen heißen oft so), ein Abschneiden
 * am `#` wie bei einem Anker nahm solchen Dateien das „Ansehen".
 */
export const isViewablePdf = (name?: string | null, mimeType?: string | null): boolean =>
    mimeType === 'application/pdf' || /\.pdf$/i.test(String(name ?? '').trim());

/**
 * Text, den der Betrachter selbst darstellt: Markdown und reiner Text. Die Normtexte der
 * Regelwerke (Quellen einer Pflichten-Auswertung) sind `.md` — ohne das öffnete ein neuer Tab die
 * rohe Datei, die der Browser je nach Laune zeigte oder herunterlud (30.09.2026).
 */
export const isViewableText = (name?: string | null, mimeType?: string | null): boolean =>
    mimeType === 'text/markdown' || mimeType === 'text/plain' || /\.(md|markdown|txt)$/i.test(String(name ?? '').trim());

/** Alles, was der Betrachter an der Seite zeigt — PDF oder Text. */
export const isViewableDocument = (name?: string | null, mimeType?: string | null): boolean =>
    isViewablePdf(name, mimeType) || isViewableText(name, mimeType);

/**
 * Die Dokumente der Konversation, in der eine Antwort steht — woran `rehypeDocumentCitations`
 * Nennungen erkennt. Gesetzt von `ChatExchange`.
 */
export const ConversationDocumentsContext = React.createContext<ConversationDocument[]>([]);

const DocumentViewerPanel = React.lazy(() => import('./DocumentViewerPanel'));

/**
 * Wer den Betrachter **andockt** statt ihn schweben zu lassen. Die AppShell meldet sich an
 * (`registerDock`) und zeigt das offene Dokument als rechte Spalte neben dem Chat — als Overlay
 * deckte es auf einem 14-Zoll-Bildschirm den Chat bis auf rund 200 px zu, und Antwort und Quelle
 * ließen sich nicht nebeneinander lesen (Sasse, 06.10.2026). Ohne angemeldeten Platz (mobil,
 * Seiten außerhalb der Shell) bleibt es beim Overlay.
 */
export interface DocumentViewerDock {
    /** Das offene Dokument; `null`, wenn keins offen ist. */
    open: { request: OpenDocumentRequest; generation: number } | null;
    close: () => void;
    openDocument: (request: OpenDocumentRequest) => void;
    /** Meldet einen Andockplatz an; die Rückgabe meldet ihn wieder ab. */
    registerDock: () => () => void;
}

const DocumentViewerDockContext = React.createContext<DocumentViewerDock | null>(null);

export const useDocumentViewerDock = () => React.useContext(DocumentViewerDockContext);

export function DocumentViewerProvider({ children }: { children: React.ReactNode }) {
    const [request, setRequest] = React.useState<OpenDocumentRequest | null>(null);
    // Jedes Öffnen ist ein neuer Auftrag, auch mit derselben Datei — sonst spränge der Betrachter
    // bei einer anderen Seite im selben Dokument nicht noch einmal.
    const [generation, setGeneration] = React.useState(0);
    const [docks, setDocks] = React.useState(0);
    const api = React.useMemo<DocumentViewerApi>(
        () => ({
            openDocument: next => {
                setRequest(next);
                setGeneration(g => g + 1);
            },
        }),
        []
    );
    const close = React.useCallback(() => setRequest(null), []);
    const registerDock = React.useCallback(() => {
        setDocks(n => n + 1);
        return () => setDocks(n => n - 1);
    }, []);
    const dock = React.useMemo<DocumentViewerDock>(
        () => ({
            open: request ? { request, generation } : null,
            close,
            openDocument: api.openDocument,
            registerDock,
        }),
        [request, generation, close, api, registerDock]
    );
    return (
        <DocumentViewerContext.Provider value={api}>
            <DocumentViewerDockContext.Provider value={dock}>
                {children}
                {request && docks === 0 && (
                    <React.Suspense fallback={null}>
                        <DocumentViewerPanel
                            key={`${request.fileId}:${generation}`}
                            request={request}
                            onClose={close}
                            onOpenDocument={api.openDocument}
                        />
                    </React.Suspense>
                )}
            </DocumentViewerDockContext.Provider>
        </DocumentViewerContext.Provider>
    );
}
